<template>
  <div class="page-content add-payment-method-wrapper">
    <div class="logo-container">
      <img
        alt="gogo logo"
        :src="logoUrl"
      >
    </div>
    <add-credit-card
      v-if="isPageReady"
      class="px-4 mt-2"
      :plan="planFromLink"
    />
    <message-html
      class="mt-4"
      :result-html="errorHtml"
      :has-top-line="false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { callerService } from '@/services/caller';
import { pushNotificationService } from '@/services/pushNotification';
import { PushNotificationCode } from '@/models/app';
import { gogoMainNumber } from '@/constants/app';
import logoUrl from '@/assets/img/logo-gogo-cornflower.svg';

const AddCreditCard = defineAsyncComponent(() => import('@/components/UIComponents/Payments/ManuallyAddCreditCard.vue') as any);

const route = useRoute();

const { setCustomerInfo, customerFullName } = useCustomer();
const { fetchCurrentUser } = useProfile();
const { showErrorPopupDialog } = useSwalDialog();

const instance: any = getCurrentInstance();
const errorHtml = ref('');
const isPageReady = ref(false);

// The membership the operator picked when sending the link, e.g. /add-payment-method/<token>?plan=basic15
const planFromLink = computed(() => {
  const plan = Array.isArray(route.query.plan) ? route.query.plan[0] : route.query.plan;
  return plan?.trim() || undefined;
});

const initGooglePlaces = async () => instance.ctx.$gmapApiPromiseLazy();

const loadCustomer = async () => {
  const accessToken = route.params.token as string;

  if (!accessToken) {
    return;
  }

  localStorage.setItem('access_token', accessToken);

  try {
    const response = await fetchCurrentUser();

    const customer = await callerService.fetchCustomerInfo({
      phoneNumber: response.phoneNumber!,
    });

    setCustomerInfo(customer);
    isPageReady.value = true;

    pushNotificationService.sendPushNotificationOnLiveCall({
      subject: `Add card link accessed by ${customerFullName.value}`,
      message: 'Customer opened the secure card link',
      code: PushNotificationCode.ClientCardLinkAccessed,
    }).catch(() => {});
  } catch (err: any) {
    localStorage.removeItem('access_token');
    const networkErrorResult = err.networkError?.result;

    if (networkErrorResult.code === 401) {
      errorHtml.value = util.getErrorResponse({
        message: `The link has expired.<br />Please try again or call an operator on <a href="tel:${gogoMainNumber}">${gogoMainNumber}</a> to create a new one.`,
      });
      return;
    }

    errorHtml.value = util.getGraphqlError(err);
  }
};

onMounted(async () => {
  const spinner = ElLoading.service({
    lock: true,
    text: 'Loading information...',
    spinner: 'el-icon-loading',
    background: 'rgba(0, 0, 0, 0.7)',
  });

  try {
    await initGooglePlaces();
    await loadCustomer();
  } catch (error: any) {
    showErrorPopupDialog({
      text: 'An error had occured. Please try again later.',
    });
  } finally {
    spinner.close();
  }
});
</script>

<style lang="scss">
.add-payment-method-wrapper {
  margin: 0 auto 20px;
  #add-credit-card {
    max-width: 500px;
    margin: 0 auto;
  }

  .logo-container {
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 10;
    background: white;
    min-height: 56px;
    padding: 20px 10px 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

    img {
      width: 200px;
    }
  }

  .result-message {
    padding: 0 10px;
    a {
      font-weight: bold;
      color: #6d509c;
    }
  }
}
</style>
